// A workflow's metadata (docs/03 "WorkflowTemplate"): name, slug, task type, description and
// icon. The create drawer and the editor's Settings tab share it.
import { type FormEvent, useState } from "react";
import { Button, Input, Textarea } from "rizzui";

import { useT } from "../i18n";
import { Hint, SelectField } from "../ui/SideDrawer";
import { ErrorState } from "../ui/states";
import { iconOptions } from "./iconNames";
import { slugify, slugProblem } from "./slug";
import type { TaskType, WorkflowInput } from "./types";

interface Props {
  initial?: WorkflowInput;
  taskTypes: TaskType[];
  submitLabel: string;
  onSubmit: (input: WorkflowInput) => Promise<void>;
  onCancel?: () => void;
}

export function WorkflowForm({ initial, taskTypes, submitLabel, onSubmit, onCancel }: Props) {
  const { catalog, common } = useT();
  const f = catalog.form;
  const t = catalog.workflowForm;
  const [form, setForm] = useState<WorkflowInput>(
    () => initial ?? { name: "", slug: "", description: "", task_type_id: taskTypes[0]?.id ?? "", icon: "flow-arrow" },
  );
  const [slugTouched, setSlugTouched] = useState(initial !== undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const set = (patch: Partial<WorkflowInput>) => setForm((f) => ({ ...f, ...patch }));
  const badSlug = form.slug ? slugProblem(form.slug) : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-5" onSubmit={(e) => void submit(e)} aria-label={t.label}>
      <Input
        label={f.name}
        value={form.name}
        required
        onChange={(e) => set({ name: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })}
      />
      <div>
        <Input
          label={f.slug}
          value={form.slug}
          required
          error={badSlug}
          onChange={(e) => {
            setSlugTouched(true);
            set({ slug: e.target.value });
          }}
        />
        {badSlug ? null : <Hint>{t.slugHint}</Hint>}
      </div>
      <SelectField
        label={t.taskType}
        value={form.task_type_id}
        options={taskTypes.map((tt) => ({ value: tt.id, label: tt.enabled ? tt.name : t.disabledType(tt.name) }))}
        onChange={(task_type_id) => set({ task_type_id })}
      />
      <Textarea
        label={f.description}
        value={form.description}
        rows={3}
        placeholder={t.descriptionPlaceholder}
        onChange={(e) => set({ description: e.target.value })}
      />
      <SelectField label={f.icon} value={form.icon ?? ""} options={iconOptions(f.noIcon)} onChange={(v) => set({ icon: v || null })} />
      {error ? <ErrorState title={f.notSaved} message={error} /> : null}
      <div className="flex justify-end gap-3 border-t border-muted pt-4">
        {onCancel ? (
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            {common.actions.cancel}
          </Button>
        ) : null}
        <Button type="submit" isLoading={busy} disabled={!!badSlug || !form.task_type_id}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
